import { type AssistantFinding, type AssistantMessage, isRecord, list, messages, normaliseFinding, normaliseMessage, num, str } from './api';

/**
 * Round-2 ris shapes (viewer-ai contract v2 §3–§4), normalised like api.ts does for messages: the
 * panel reads only what was checked here. Large-study jobs, structured reports, similar cases.
 */

export const JOB_DEPTHS = ['screen', 'thorough'] as const;
export type JobDepth = (typeof JOB_DEPTHS)[number];

export const JOB_STATUSES = ['queued', 'running', 'done', 'failed', 'cancelled'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export interface JobStart {
  jobId: string;
  cached: boolean;
}

export interface JobProgress {
  done: number;
  total: number;
  stage: string | null;
}

export interface AnalysisJob {
  id: string;
  status: JobStatus;
  progress: JobProgress;
  runId: string | null;
  error: string | null;
  findings: AssistantFinding[];
  /** What ris actually analysed (slices sent / skipped and why, windows, model tier); null on older ris. */
  summary: string | null;
}

/**
 * A series this small (an X-ray's one or two views) is reviewed by the plain sweep question, each image
 * at full size: the large-study job shrinks every slice to a mosaic tile, too small to read a radiograph.
 */
export const DIRECT_REVIEW_MAX_IMAGES = 2;

export function reviewsDirectly(instanceCount: number): boolean {
  return instanceCount >= 1 && instanceCount <= DIRECT_REVIEW_MAX_IMAGES;
}

export const REPORT_SYSTEMS = ['auto', 'BI-RADS', 'Lung-RADS', 'TI-RADS', 'LI-RADS'] as const;
export type ReportSystem = (typeof REPORT_SYSTEMS)[number];

export const REPORT_SECTION_KEYS = ['findings', 'impression', 'recommendation'] as const;
export type ReportSectionKey = (typeof REPORT_SECTION_KEYS)[number];

export interface ReportCitation {
  guideline: string;
  text: string;
}

export interface StructuredReport {
  system: string | null;
  category: string | null;
  sections: Partial<Record<ReportSectionKey, string>>;
  citations: ReportCitation[];
  model: string | null;
}

export interface SimilarCase {
  studyUid: string;
  dateRelative: string | null;
  modality: string | null;
  snippet: string;
  score: number | null;
}

function jobStatus(value: unknown): JobStatus {
  return (JOB_STATUSES as readonly unknown[]).includes(value) ? (value as JobStatus) : 'queued';
}

export function normaliseProgress(value: unknown): JobProgress {
  const raw = isRecord(value) ? value : {};
  const total = Math.max(0, num(raw.total) ?? 0);
  const done = Math.min(Math.max(0, num(raw.done) ?? 0), total || Number.MAX_SAFE_INTEGER);
  return { done, total, stage: str(raw.stage) };
}

export function normaliseJobStart(value: unknown): JobStart | null {
  if (!isRecord(value)) return null;
  const jobId = str(value.job_id) ?? str(value.id);
  return jobId ? { jobId, cached: value.cached === true } : null;
}

export function normaliseJob(value: unknown): AnalysisJob | null {
  if (!isRecord(value)) return null;
  const id = str(value.id) ?? str(value.job_id);
  if (!id) return null;
  return {
    id,
    status: jobStatus(value.status),
    progress: normaliseProgress(value.progress),
    runId: str(value.run_id),
    error: str(value.error),
    findings: list(value.findings, normaliseFinding),
    summary: str(value.summary),
  };
}

function citation(value: unknown): ReportCitation | null {
  if (!isRecord(value)) return null;
  const text = str(value.text);
  const guideline = str(value.guideline);
  return text || guideline ? { guideline: guideline ?? '', text: text ?? '' } : null;
}

/** `{report: {...}, model}` (the route) or the bare report; null when nothing usable came back. */
export function normaliseReport(value: unknown): StructuredReport | null {
  if (!isRecord(value)) return null;
  const report = isRecord(value.report) ? value.report : value;
  const rawSections = isRecord(report.sections) ? report.sections : {};
  const sections: Partial<Record<ReportSectionKey, string>> = {};
  for (const key of REPORT_SECTION_KEYS) {
    const text = str(rawSections[key]);
    if (text) sections[key] = text;
  }
  const out: StructuredReport = {
    system: str(report.system),
    category: str(report.category),
    sections,
    citations: list(report.citations, citation),
    model: str(value.model),
  };
  return out.category || Object.keys(sections).length > 0 ? out : null;
}

function similarCase(value: unknown): SimilarCase | null {
  if (!isRecord(value)) return null;
  const studyUid = str(value.study_uid);
  if (!studyUid) return null;
  return {
    studyUid,
    dateRelative: str(value.date_relative),
    modality: str(value.modality),
    snippet: str(value.snippet) ?? '',
    score: num(value.score),
  };
}

export function normaliseSimilar(value: unknown): SimilarCase[] {
  return list(isRecord(value) ? value.items : value, similarCase);
}

/**
 * The `final` event of `/assistant/stream`: the same Message shape as the non-stream route. ris may
 * send the pair (question + answer, like POST /assistant), the answer alone, or `{messages: [...]}`;
 * all three read the same here.
 */
export function finalMessages(value: unknown): AssistantMessage[] {
  if (Array.isArray(value)) return messages(value);
  if (isRecord(value) && Array.isArray(value.messages)) return messages(value.messages);
  const one = normaliseMessage(value);
  return one ? [one] : [];
}
